import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';

export interface ListTab {
  key: string;
  label: string;
}

/**
 * Pack B2 list scaffold: optional search, segmented filter tabs, a body of
 * full-width tappable rows, and a primary create action (FAB on mobile,
 * header button on desktop). Empty states render centered, never bare text.
 */
export function ListScreen({
  title,
  tabs,
  activeTab,
  onTabChange,
  search,
  primaryAction,
  secondaryAction,
  filters,
  isEmpty,
  empty,
  children
}: {
  title?: string;
  tabs?: ListTab[];
  activeTab?: string;
  onTabChange?: (key: string) => void;
  search?: { value: string; onChange: (v: string) => void; placeholder?: string };
  primaryAction?: { label: string; onClick?: () => void; to?: string };
  secondaryAction?: { label: string; to?: string; onClick?: () => void };
  /** Optional secondary filter controls (chips) rendered between tabs and the list. */
  filters?: ReactNode;
  isEmpty?: boolean;
  empty?: { icon?: ReactNode; message: string; action?: ReactNode };
  children: ReactNode;
}) {
  return (
    <div className="mx-auto max-w-3xl px-4 py-5 pb-24 sm:pb-8">
      {(title || primaryAction || secondaryAction) && (
        <div className="mb-4 flex items-center justify-between gap-3">
          {title && <h1 className="font-display text-2xl font-semibold text-ink">{title}</h1>}
          <div className="flex items-center gap-3">
            {secondaryAction &&
              (secondaryAction.to ? (
                <Link to={secondaryAction.to} className="text-sm font-medium text-pine hover:text-pine-600">
                  {secondaryAction.label}
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={secondaryAction.onClick}
                  className="text-sm font-medium text-pine hover:text-pine-600"
                >
                  {secondaryAction.label}
                </button>
              ))}
            {primaryAction && (
              <div className="hidden sm:block">
                <PrimaryButton action={primaryAction} />
              </div>
            )}
          </div>
        </div>
      )}

      {search && (
        <label className="mb-3 flex items-center gap-2 rounded-xl bg-surface px-3 shadow-sm ring-1 ring-slate-200/70">
          <Search size={16} className="text-slate-400" />
          <input
            className="min-h-[44px] w-full bg-transparent text-sm text-ink placeholder:text-slate-400 focus:outline-none"
            placeholder={search.placeholder ?? 'Search'}
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
          />
        </label>
      )}

      {tabs && tabs.length > 0 && (
        <div className="mb-4 flex gap-1 overflow-x-auto whitespace-nowrap" role="tablist" aria-label="Filter">
          {tabs.map((t) => {
            const active = t.key === activeTab;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onTabChange?.(t.key)}
                className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                  active ? 'bg-pine text-white font-medium' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      )}

      {filters && <div className="mb-3 flex flex-wrap items-center gap-2">{filters}</div>}

      {isEmpty && empty ? (
        <EmptyState icon={empty.icon} message={empty.message} action={empty.action} />
      ) : (
        <div className="overflow-hidden rounded-2xl bg-surface shadow-sm ring-1 ring-slate-200/70 divide-y divide-slate-100">
          {children}
        </div>
      )}

      {/* Mobile FAB — sits above the bottom nav. */}
      {primaryAction && (
        <div className="sm:hidden">
          {primaryAction.to ? (
            <Link
              to={primaryAction.to}
              aria-label={primaryAction.label}
              className="fixed bottom-20 right-5 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-pine text-white shadow-lg active:bg-pine-800"
            >
              <Plus size={24} />
            </Link>
          ) : (
            <button
              type="button"
              onClick={primaryAction.onClick}
              aria-label={primaryAction.label}
              className="fixed bottom-20 right-5 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-pine text-white shadow-lg active:bg-pine-800"
            >
              <Plus size={24} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PrimaryButton({ action }: { action: { label: string; onClick?: () => void; to?: string } }) {
  const cls =
    'inline-flex items-center gap-1.5 rounded-lg bg-pine px-4 min-h-[44px] text-sm font-medium text-white hover:bg-pine-600';
  return action.to ? (
    <Link to={action.to} className={cls}>
      <Plus size={16} /> {action.label}
    </Link>
  ) : (
    <button type="button" onClick={action.onClick} className={cls}>
      <Plus size={16} /> {action.label}
    </button>
  );
}

/** A full-width tappable list row: primary text left, optional money (mono) right, muted secondary line. */
export function ListRow({
  to,
  onClick,
  primary,
  secondary,
  right,
  rightSub,
  badge
}: {
  to?: string;
  onClick?: () => void;
  primary: ReactNode;
  secondary?: ReactNode;
  right?: ReactNode;
  rightSub?: ReactNode;
  badge?: ReactNode;
}) {
  const inner = (
    <>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate font-medium text-ink">{primary}</span>
          {badge}
        </span>
        {secondary && <span className="mt-0.5 block truncate text-sm text-slate-500">{secondary}</span>}
      </span>
      {(right != null || rightSub != null) && (
        <span className="shrink-0 text-right">
          {right != null && <span className="block font-mono text-sm text-ink">{right}</span>}
          {rightSub != null && <span className="mt-0.5 block text-xs text-slate-400">{rightSub}</span>}
        </span>
      )}
    </>
  );
  const cls = 'flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50';
  if (to) return <Link to={to} className={cls}>{inner}</Link>;
  return (
    <button type="button" onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}

/** A small pill toggle for secondary filters (kind, deactivated-visible, …). */
export function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full border px-3 py-1 text-xs transition-colors ${
        active ? 'border-pine bg-slate-50 text-pine font-medium' : 'border-slate-200 text-slate-500 hover:bg-slate-50'
      }`}
    >
      {children}
    </button>
  );
}

export function EmptyState({ icon, message, action }: { icon?: ReactNode; message: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl bg-surface px-6 py-16 text-center shadow-sm ring-1 ring-slate-200/70">
      {icon && <div className="text-slate-300">{icon}</div>}
      <p className="text-sm text-slate-500">{message}</p>
      {action}
    </div>
  );
}
